import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRightLeft, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MediaAttachBar from "@/components/MediaAttachBar";
import MediaCarousel from "@/components/MediaCarousel";
import PostAttachmentChips from "@/components/PostAttachmentChips";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "@/components/RichMarkdownEditor";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import {
  deletePostAttachment,
  resolvePostAttachmentSrc,
  uploadPostAttachments,
} from "@/lib/postAttachments";
import { toastError } from "@/lib/toast";
import { getFullName } from "@/lib/userUtils";
import {
  FIBONACCI_POINTS,
  parentIdOf,
  storyPointsOf,
  workKindOf,
} from "@/lib/workKind";

const MAX_CARD_ASSIGNEES = 5;

const DEFAULT_LANES = ["Backlog", "To Do", "In Progress", "In Review", "Done"];

/** YYYY-MM-DD for <input type="date"> from API datetime (local calendar). */
export function toDateInputValue(raw) {
  if (!raw) return "";
  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local noon ISO from date input — avoids UTC-midnight day shift. */
export function dateInputToIso(dateOnly) {
  if (!dateOnly) return undefined;
  const [y, m, d] = dateOnly.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

export function formFromCard(card) {
  if (!card) {
    return {
      title: "",
      content: "",
      kanban_status: "To Do",
      assignee_ids: [],
      due_date: "",
      starts_at: "",
      priority: "medium",
      sprint_id: "",
      work_kind: "task",
      parent_id: "",
      story_points: "",
    };
  }
  const fromIds = card.assignee_ids || card.assigneeIds;
  let assignee_ids = [];
  if (Array.isArray(fromIds) && fromIds.length) {
    assignee_ids = [...fromIds];
  } else {
    const users = card.assignees;
    if (Array.isArray(users) && users.length) {
      assignee_ids = users.map((u) => u.user_id || u.userId).filter(Boolean);
    }
  }
  return {
    title: card.title || "",
    content: card.content || card.body || "",
    kanban_status: card.kanbanStatus || card.kanban_status || "To Do",
    assignee_ids,
    due_date: toDateInputValue(card.dueDate || card.due_date),
    starts_at: toDateInputValue(card.startsAt || card.starts_at),
    priority: card.priority || "medium",
    sprint_id: String(card.sprint_id ?? card.sprintId ?? ""),
    work_kind: workKindOf(card),
    parent_id: parentIdOf(card) ? String(parentIdOf(card)) : "",
    story_points:
      storyPointsOf(card) != null ? String(storyPointsOf(card)) : "",
  };
}

/**
 * Search-and-select assignees for large teams (chips + filtered clickable list).
 */
export function CardAssigneesPicker({
  teamMembers = [],
  value = [],
  onChange,
  max = MAX_CARD_ASSIGNEES,
  disabled = false,
  idPrefix = "card-assignee",
}) {
  const [query, setQuery] = useState("");
  const selectedIds = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );
  const atCap = selectedIds.length >= max;

  const memberById = useMemo(() => {
    const map = new Map();
    for (const m of teamMembers) {
      const id = m.user_id || m.userId;
      if (id) map.set(id, m);
    }
    return map;
  }, [teamMembers]);

  const selectedMembers = useMemo(
    () => selectedIds.map((id) => memberById.get(id)).filter(Boolean),
    [selectedIds, memberById]
  );

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const selected = new Set(selectedIds);
    return teamMembers.filter((m) => {
      const id = m.user_id || m.userId;
      if (!id || selected.has(id)) return false;
      const name = getFullName(m).toLowerCase();
      const email = String(m.org_email || m.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [teamMembers, selectedIds, query]);

  const addAssignee = (uid) => {
    if (disabled || atCap || selectedIds.includes(uid)) return;
    onChange([...selectedIds, uid]);
    setQuery("");
  };

  const removeAssignee = (uid) => {
    if (disabled) return;
    onChange(selectedIds.filter((id) => id !== uid));
  };

  const qTrim = query.trim();

  return (
    <div>
      <Label htmlFor={`${idPrefix}-search`}>Assignees</Label>
      {selectedMembers.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {selectedMembers.map((m) => {
            const uid = m.user_id || m.userId;
            return (
              <span
                key={uid}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 pl-1.5 pr-1 py-0.5 text-sm max-w-full"
              >
                <OrgMemberAvatar
                  user={m}
                  className="h-5 w-5 shrink-0"
                  fallbackClassName="text-[9px]"
                />
                <span className="truncate max-w-[10rem]">{getFullName(m)}</span>
                {!disabled ? (
                  <button
                    type="button"
                    className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                    aria-label={`Remove ${getFullName(m)}`}
                    onClick={() => removeAssignee(uid)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}
      {!disabled && !atCap ? (
        <>
          <Input
            id={`${idPrefix}-search`}
            className="mt-1.5"
            placeholder="Search team members…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            disabled={disabled}
          />
          {teamMembers.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-1.5">No team members</p>
          ) : !qTrim ? (
            <p className="text-xs text-muted-foreground mt-1.5">
              Type a name to find teammates.
            </p>
          ) : filteredMembers.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-1.5">
              {`No members match “${qTrim}”.`}
            </p>
          ) : (
            <ul
              className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border"
              role="listbox"
              aria-label="Matching team members"
            >
              {filteredMembers.map((m) => {
                const uid = m.user_id || m.userId;
                return (
                  <li key={uid}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60 flex items-center gap-2"
                      onClick={() => addAssignee(uid)}
                    >
                      <OrgMemberAvatar
                        user={m}
                        className="h-6 w-6 shrink-0"
                        fallbackClassName="text-[10px]"
                      />
                      <span className="truncate">{getFullName(m)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
      {atCap && !disabled ? (
        <p className="text-xs text-muted-foreground mt-1.5">
          Maximum of {max} assignees reached.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground mt-1.5">
          Up to {max} people. {selectedIds.length}/{max} selected.
        </p>
      )}
    </div>
  );
}

async function syncCardSprintMembership({
  accessToken,
  orgId,
  teamId,
  postId,
  nextSprintId,
  prevSprintId,
}) {
  const next = nextSprintId ? Number(nextSprintId) : null;
  const prev = prevSprintId ? Number(prevSprintId) : null;
  const nextNorm = Number.isFinite(next) && next > 0 ? next : null;
  const prevNorm = Number.isFinite(prev) && prev > 0 ? prev : null;
  if (nextNorm === prevNorm || !teamId) return;
  const headers = { Authorization: `Bearer ${accessToken}` };
  const params = { org_id: orgId };
  if (nextNorm) {
    await axios.post(
      `${API_BASE_URL}/team/${teamId}/sprints/${nextNorm}/posts/${postId}`,
      {},
      { headers, params }
    );
  } else if (prevNorm) {
    await axios.delete(
      `${API_BASE_URL}/team/${teamId}/sprints/${prevNorm}/posts/${postId}`,
      { headers, params }
    );
  }
}

export default function CardEditDialog({
  open,
  onOpenChange,
  card,
  onCardChange,
  orgId,
  teamId,
  accessToken,
  kanbanLanes = [],
  sprints = [],
  teamMembers = [],
  parentCandidates = [],
  canManageOrgSettings = false,
  onAddChild,
  showHandoff = false,
  onHandoff,
  onCancelPendingHandoff,
  cancellingHandoff = false,
  onDelete,
  onSaved,
}) {
  const [formData, setFormData] = useState(() => formFromCard(card));
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);
  const [saving, setSaving] = useState(false);

  const cardId = card?.post_id || card?.postId;

  useEffect(() => {
    if (open && card) {
      setFormData(formFromCard(card));
      setPendingFiles([]);
    }
    // Sync form when dialog opens or card id changes; ignore card object identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cardId]);

  const parentPickerOptions = useMemo(() => {
    const kind = formData.work_kind || "task";
    const selfId = cardId != null ? Number(cardId) : null;
    return (parentCandidates || []).filter((p) => {
      const id = Number(p.post_id || p.postId);
      if (selfId && id === selfId) return false;
      const k = workKindOf(p);
      if (kind === "story") return k === "epic";
      if (kind === "task") return k === "epic" || k === "story";
      return false;
    });
  }, [parentCandidates, formData.work_kind, cardId]);

  const changeWorkKind = (next) => {
    setFormData((prev) => {
      let parent_id = prev.parent_id;
      if (next === "epic") {
        parent_id = "";
      } else if (parent_id) {
        const parent = (parentCandidates || []).find(
          (p) => String(p.post_id || p.postId) === String(parent_id)
        );
        const pk = parent ? workKindOf(parent) : null;
        if (next === "story" && pk !== "epic") parent_id = "";
        if (next === "task" && pk !== "epic" && pk !== "story") parent_id = "";
      }
      return {
        ...prev,
        work_kind: next,
        parent_id,
        story_points: next === "epic" ? "" : prev.story_points,
        sprint_id: next === "epic" ? "" : prev.sprint_id,
      };
    });
  };

  const cardFrozen = Boolean(card?.handoff_frozen || card?.pending_handoff);
  const pending = card?.pending_handoff || card?.pendingHandoff;
  const history = card?.handoff_history || card?.handoffHistory || [];
  const disabledFieldProps = cardFrozen ? { disabled: true } : {};

  const handleSave = async () => {
    const content = normalizeEditorMarkdown(formData.content);
    if (!card || !formData.title?.trim() || isMarkdownEmpty(content)) {
      toastError("Title and content are required");
      return;
    }
    if (cardFrozen) {
      toastError("This card is frozen while a handoff is pending.");
      return;
    }
    if (!accessToken || !orgId || !cardId) return;

    setSaving(true);
    try {
      const prevSprintId = card.sprint_id ?? card.sprintId ?? null;
      await axios.patch(
        `${API_BASE_URL}/posts/${cardId}`,
        {
          title: formData.title.trim(),
          content,
          kanban_status: formData.kanban_status,
          assignee_ids: Array.isArray(formData.assignee_ids)
            ? formData.assignee_ids
            : [],
          due_date: dateInputToIso(formData.due_date) ?? null,
          starts_at: dateInputToIso(formData.starts_at) ?? null,
          priority: formData.priority,
          work_kind: formData.work_kind || "task",
          parent_id:
            formData.work_kind === "epic" || !formData.parent_id
              ? null
              : Number(formData.parent_id),
          story_points:
            formData.work_kind === "epic" || !formData.story_points
              ? null
              : Number(formData.story_points),
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: orgId },
        }
      );

      await syncCardSprintMembership({
        accessToken,
        orgId,
        teamId,
        postId: cardId,
        nextSprintId:
          formData.work_kind === "epic" ? null : formData.sprint_id || null,
        prevSprintId,
      });

      if (pendingFiles.length > 0) {
        try {
          await uploadPostAttachments({
            postId: cardId,
            orgId,
            files: pendingFiles,
            accessToken,
          });
        } catch (attachErr) {
          console.error("Failed to upload card attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Card updated, but file upload failed",
            { canManageSettings: canManageOrgSettings, orgId }
          );
        }
      }

      setPendingFiles([]);
      await onSaved?.();
    } catch (err) {
      console.error("Failed to update card:", err);
      toastError(err, "Failed to update card");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange?.(next);
        if (!next) setPendingFiles([]);
      }}
    >
      {open && card ? (
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto z-[60]"
          overlayClassName="z-[60]"
        >
          <DialogHeader>
            <DialogTitle>Edit work</DialogTitle>
            <DialogDescription>
              Update type, parent, schedule, and board fields
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cardFrozen && (
              <Alert>
                <AlertDescription>
                  This card is frozen while a handoff is pending. You can still
                  open the thread to comment. Edits and lane moves are disabled
                  until the handoff is accepted, rejected, or cancelled.
                </AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="edit-card-work-kind">Type</Label>
              <select
                id="edit-card-work-kind"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.work_kind || "task"}
                onChange={(e) => changeWorkKind(e.target.value)}
                {...disabledFieldProps}
              >
                <option value="task">Task</option>
                <option value="story">Story</option>
                <option value="epic">Epic</option>
              </select>
            </div>
            {formData.work_kind !== "epic" && (
              <div>
                <Label htmlFor="edit-card-parent">Parent</Label>
                <select
                  id="edit-card-parent"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.parent_id || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, parent_id: e.target.value })
                  }
                  {...disabledFieldProps}
                >
                  <option value="">None</option>
                  {parentPickerOptions.map((p) => {
                    const id = p.post_id || p.postId;
                    return (
                      <option key={id} value={String(id)}>
                        {workKindOf(p) === "epic" ? "Epic" : "Story"}: {p.title}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {formData.work_kind !== "epic" && (
              <div>
                <Label htmlFor="edit-card-points">Story points</Label>
                <select
                  id="edit-card-points"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.story_points || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, story_points: e.target.value })
                  }
                  {...disabledFieldProps}
                >
                  <option value="">None</option>
                  {FIBONACCI_POINTS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(formData.work_kind === "epic" || formData.work_kind === "story") &&
              !cardFrozen &&
              onAddChild && (
                <div className="flex flex-wrap gap-2">
                  {formData.work_kind === "epic" ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onAddChild("story")}
                    >
                      Add story
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onAddChild("task")}
                  >
                    Add task
                  </Button>
                </div>
              )}
            <div>
              <Label htmlFor="edit-card-title">Title *</Label>
              <Input
                id="edit-card-title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter card title"
                {...disabledFieldProps}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-card-content">Content *</Label>
              <RichMarkdownEditor
                id="edit-card-content"
                key={cardId}
                value={formData.content}
                onChange={(next) =>
                  setFormData((prev) => ({ ...prev, content: next }))
                }
                onSubmit={handleSave}
                submitMode="mod-enter"
                variant="compact"
                minRows={4}
                placeholder="Enter card description"
                disabled={cardFrozen || saving}
              />
            </div>
            <div className="space-y-2">
              <Label>Attachments</Label>
              {(() => {
                const atts = Array.isArray(card.attachments)
                  ? card.attachments
                  : [];
                const mediaAtts = atts.filter(
                  (a) => a.is_image || a.is_video || a.isImage || a.isVideo
                );
                const chipAtts = atts.filter(
                  (a) =>
                    !(a.is_image || a.is_video || a.isImage || a.isVideo)
                );
                const mediaItems = mediaAtts.map((a) => ({
                  type: a.is_video || a.isVideo ? "video" : "image",
                  attachmentId: a.attachment_id ?? a.attachmentId,
                  alt: a.file_name || a.fileName,
                }));
                return (
                  <div className="space-y-2">
                    {mediaItems.length > 0 ? (
                      <MediaCarousel
                        className="max-w-xl"
                        items={mediaItems}
                        resolveSrc={(item) =>
                          resolvePostAttachmentSrc({
                            postId: cardId,
                            attachmentId: item.attachmentId,
                            orgId,
                            accessToken,
                          })
                        }
                      />
                    ) : null}
                    {chipAtts.length > 0 ? (
                      <PostAttachmentChips
                        attachments={chipAtts}
                        postId={cardId}
                        orgId={orgId}
                        accessToken={accessToken}
                      />
                    ) : null}
                    {atts.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No files attached yet.
                      </p>
                    ) : null}
                    {!cardFrozen && atts.length > 0 ? (
                      <ul className="space-y-1">
                        {atts.map((att) => {
                          const aid = att.attachment_id ?? att.attachmentId;
                          return (
                            <li
                              key={aid}
                              className="flex items-center justify-between gap-2 text-xs"
                            >
                              <span className="truncate text-muted-foreground">
                                {att.file_name || att.fileName || "file"}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 shrink-0 px-2"
                                disabled={
                                  removingAttachmentId === aid || saving
                                }
                                onClick={async () => {
                                  setRemovingAttachmentId(aid);
                                  try {
                                    await deletePostAttachment({
                                      postId: cardId,
                                      attachmentId: aid,
                                      orgId,
                                      accessToken,
                                    });
                                    onCardChange?.(
                                      card
                                        ? {
                                            ...card,
                                            attachments: (
                                              card.attachments || []
                                            ).filter(
                                              (a) =>
                                                (a.attachment_id ??
                                                  a.attachmentId) !== aid
                                            ),
                                          }
                                        : card
                                    );
                                  } catch (err) {
                                    toastError(
                                      err,
                                      "Failed to remove attachment"
                                    );
                                  } finally {
                                    setRemovingAttachmentId(null);
                                  }
                                }}
                              >
                                {removingAttachmentId === aid
                                  ? "Removing…"
                                  : "Remove"}
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                    {!cardFrozen ? (
                      <MediaAttachBar
                        mode="files"
                        pendingFiles={pendingFiles}
                        onFilesChange={setPendingFiles}
                        disabled={saving}
                      />
                    ) : null}
                  </div>
                );
              })()}
            </div>
            <div>
              <Label htmlFor="edit-card-status">Status</Label>
              <select
                id="edit-card-status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.kanban_status}
                onChange={(e) =>
                  setFormData({ ...formData, kanban_status: e.target.value })
                }
                {...disabledFieldProps}
              >
                {kanbanLanes.length > 0 ? (
                  [...kanbanLanes]
                    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                    .map((lane) => (
                      <option key={lane.lane_id} value={lane.name}>
                        {lane.name}
                      </option>
                    ))
                ) : (
                  DEFAULT_LANES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <CardAssigneesPicker
                idPrefix="edit-card-assignee"
                teamMembers={teamMembers}
                value={formData.assignee_ids || []}
                onChange={(assignee_ids) =>
                  setFormData({ ...formData, assignee_ids })
                }
                disabled={!!disabledFieldProps.disabled}
              />
            </div>
            <div>
              <Label htmlFor="edit-card-starts-at">Start date</Label>
              <Input
                id="edit-card-starts-at"
                type="date"
                value={formData.starts_at || ""}
                onChange={(e) =>
                  setFormData({ ...formData, starts_at: e.target.value })
                }
                {...disabledFieldProps}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Clear to remove the Roadmap range bar (due alone stays a point).
              </p>
            </div>
            <div>
              <Label htmlFor="edit-card-due-date">Due Date</Label>
              <Input
                id="edit-card-due-date"
                type="date"
                value={formData.due_date}
                onChange={(e) =>
                  setFormData({ ...formData, due_date: e.target.value })
                }
                {...disabledFieldProps}
              />
            </div>
            <div>
              <Label htmlFor="edit-card-priority">Priority</Label>
              <select
                id="edit-card-priority"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                {...disabledFieldProps}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <Label htmlFor="edit-card-sprint">Sprint</Label>
              <select
                id="edit-card-sprint"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.sprint_id || ""}
                onChange={(e) =>
                  setFormData({ ...formData, sprint_id: e.target.value })
                }
                disabled={cardFrozen || formData.work_kind === "epic"}
              >
                <option value="">Unscheduled</option>
                {sprints.map((sp) => {
                  const sid = sp.sprint_id ?? sp.sprintId;
                  return (
                    <option key={sid} value={String(sid)}>
                      {sp.name}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.work_kind === "epic"
                  ? "Epics stay on the roadmap and cannot join a sprint."
                  : "Cards stay unscheduled until you assign a sprint."}
              </p>
            </div>
            {card.user && (
              <div className="text-sm text-muted-foreground">
                Created by {getFullName(card.user)} on{" "}
                {new Date(
                  card.created_at || card.createdAt
                ).toLocaleString()}
              </div>
            )}
            {history.length > 0 && (
              <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
                <p className="text-sm font-medium">Handoff history</p>
                <ul className="space-y-1.5">
                  {history.map((h) => {
                    const hid = h.handoff_id ?? h.handoffId;
                    const fromName =
                      h.fromTeam?.name ||
                      h.from_team?.name ||
                      `Team ${h.fromTeamId ?? h.from_team_id}`;
                    const toName =
                      h.toTeam?.name ||
                      h.to_team?.name ||
                      `Team ${h.toTeamId ?? h.to_team_id}`;
                    const when = h.createdAt || h.created_at;
                    return (
                      <li
                        key={hid}
                        className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5"
                      >
                        <Badge variant="outline" className="text-[10px] py-0">
                          {h.status}
                        </Badge>
                        <span>
                          {fromName} → {toName}
                        </span>
                        {when && (
                          <span>· {new Date(when).toLocaleDateString()}</span>
                        )}
                        {h.note && (
                          <span className="w-full text-muted-foreground/80">
                            “{h.note}”
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {showHandoff && onHandoff && !cardFrozen && (
                <Button
                  variant="outline"
                  onClick={onHandoff}
                  disabled={saving}
                >
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Handoff
                </Button>
              )}
              {cardFrozen && onCancelPendingHandoff && pending && (
                <Button
                  variant="destructive"
                  onClick={onCancelPendingHandoff}
                  disabled={cancellingHandoff || saving}
                >
                  {cancellingHandoff ? "Cancelling…" : "Cancel pending handoff"}
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="destructive"
                  onClick={onDelete}
                  disabled={saving || cancellingHandoff}
                >
                  Delete card
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={saving || cancellingHandoff}
              >
                Close
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || cancellingHandoff || cardFrozen}
              >
                {saving ? "Updating..." : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
